import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Cy_Katalog } from '@features/cypress';
import {
  PublicRestService,
  PublicSablonyVysledekDto,
  TypSablonyE,
  VyhledatPublicSablonyRequestParams,
} from '@generated/be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PublicSablonyTypStatuses } from '@reza-ais-fe/reza-ais-fe-utils';
import { IOption, UiFormsModule } from '@shared/ui/forms';
import { ButtonsComponent, StatusIndicatorComponent } from '@shared/ui/components';
import {
  AuthService,
  DEFAULT_PAGE_OPTIONS,
  downloadFile,
  Logger,
  LoggerService,
  publicApiArgs,
  scrollToFirstErrorInput,
  SignalState,
  TableRowData,
} from '@shared/utils';
import { ButtonModule } from 'primeng/button';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { SablonyFilterComponent } from '../seznam-sablon-filter/seznam-sablon-filter.component';
import { AccordionModule } from 'primeng/accordion';
import { SeznamSablonFilterFormService } from '../../../services/seznam-sablon-filter-form.service';
import { FilterValueStoreService } from '@root/libs/shared/modules/page-settings/src';
import { SeznamSablonStateService } from '../../../services/seznam-sablon-state.service';

type SeznamSablonState = {
  result: PublicSablonyVysledekDto;
  filter: VyhledatPublicSablonyRequestParams;
};

@UntilDestroy()
@Component({
  selector: 'reza-seznam-sablon-overview-filter',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonsComponent,
    ButtonModule,
    RouterModule,
    StatusIndicatorComponent,
    UiFormsModule,
    AccordionModule,
    SablonyFilterComponent,
  ],
  templateUrl: './seznam-sablon-overview-filter.component.html',
  styleUrl: './seznam-sablon-overview-filter.component.scss',
  providers: [],
})
export class SeznamSablonOverviewFilterComponent extends SignalState<SeznamSablonState> implements OnInit {
  readonly filterForm = inject(SeznamSablonFilterFormService).filterForm;
  readonly seznamSablonState = inject(SeznamSablonStateService);
  private readonly publicRestService = inject(PublicRestService);
  private readonly loggerService = inject(LoggerService);
  readonly filterValueStoreService = inject(FilterValueStoreService);
  readonly authService = inject(AuthService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);

  private logger: Logger = this.loggerService.createLogger('SeznamSablonOverviewFilterComponent');

  @Output()
  handleClearFilter = new EventEmitter();
  @Output()
  handleSubmitFilter = new EventEmitter();
  @Output()
  handleLazyLoad = new EventEmitter();

  user$ = this.authService.user$;

  public readonly publicSablonyTypStatuses = PublicSablonyTypStatuses;

  public readonly DEFAULT_PAGE_OPTION = DEFAULT_PAGE_OPTIONS;

  readonly Cy_Katalog = Cy_Katalog;

  dynamicHeight = '';

  public tableData: TableRowData<PublicSablonyVysledekDto>[] = [];
  accordionActiveIndexArr: number | number[] | null | undefined;

  ngOnInit() {
    const filterValues = this.filterValueStoreService.getFilterValues('sablony');
    if (filterValues) {
      this.filterForm.patchValue(filterValues);
    }

    this.accordionActiveIndexArr = this.isFormEmpty() ? [] : [0];

    this.seznamSablonState.connectSablony();
  }

  /**
   * Handle filter
   */
  public submitFilter() {
    if (this.filterForm.valid) {
      const filter = this.filterForm.value;

      this.logger.debug('submitFilter', filter);
      this.handleSubmitFilter.emit(filter);
      this.filterValueStoreService.setFilterValues('sablony', this.filterForm.value);
    } else {
      scrollToFirstErrorInput(this.filterForm);
    }
  }

  public clearFilter() {
    this.filterForm.reset();
    this.filterValueStoreService.clearFilterValues('sablony');

    const filter = this.filterForm.value;
    this.logger.debug('clearFilter', filter);
    this.handleClearFilter.emit(filter);
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    this.logger.debug('onLazyLoad', event);

    this.handleLazyLoad.emit(event);
  }

  /**
   * Typ select field options // todo nahradit ze souboru *.constants.ts
   */
  typOptions: IOption[] = [
    { id: TypSablonyE.Expl, label: 'Expl' },
    { id: TypSablonyE.ImplNzra, label: 'ImplNzra' },
    { id: TypSablonyE.ImplZz, label: 'ImplZz' },
  ];

  public downloadDokumentace(id: number) {
    this.publicRestService
      .getDokumentace(
        {
          id,
        },
        ...publicApiArgs()
      )
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        downloadFile(res, `${id}.pdf`);
      });
  }

  public downloadPdf(id: number) {
    this.publicRestService
      .getPdfSablony({
        id,
      })
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        downloadFile(res, `${id}.pdf`);
      });
  }

  goBack() {
    this.router.navigate(['']);
  }

  reloadData() {
    this.seznamSablonState.reloadData();
  }

  private isFormEmpty(): boolean {
    const values: any = this.filterForm.value;
    // Flatten all values (including nested objects like date ranges)
    for (const key in values) {
      const value = values[key];

      if (typeof value === 'object' && value !== null) {
        // Handle nested objects (like datepickers)
        for (const subKey in value) {
          if (value[subKey]) return false;
        }
      } else if (value) {
        return false;
      }
    }
    return true;
  }
}
